import * as THREE from "three";
import type { Line2 } from "three/examples/jsm/lines/Line2.js";
import type { LineMaterialParameters } from "three/examples/jsm/lines/LineMaterial.js";
import type { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import {
	dropDegenerateVertices,
	findSelfIntersections,
	getEdgeHelperLines,
	getEdgeNormal,
	type HelperLine,
	offsetPolyline,
	offsetPolylineEdges,
	removeSelfIntersections,
	type SelfIntersection,
} from "../../geometry";
import { Shape } from "../_base";
import { createThickLine, createThickLineSegments, INVALID_COLOR, Label, setThickLinePoints, VertexHandle } from "../_shared";

const DEFAULT_LABEL_COLOR = "#0000ff";
const DEFAULT_LINE_COLOR = "#0000ff";
const DEFAULT_VERTEX_HANDLE_COLOR = "#0000ff";

// Widths are CSS pixels — see trackCanvasResolution in _shared/utils.
const LINE_WIDTH_PX = 2;
const HIGHLIGHTED_EDGE_WIDTH_PX = 5;
const SELECTED_EDGE_WIDTH_PX = 5;

// Feedback for the edge the cursor would grab. Drawn on top of the outline, never picked itself.
const HIGHLIGHTED_EDGE_MATERIAL_DEFINITION: LineMaterialParameters = {
	color: 0x534ab7,
	linewidth: HIGHLIGHTED_EDGE_WIDTH_PX,
	transparent: true,
	depthTest: false,
	depthWrite: false,
};

// Edges picked out to be offset together. Drawn under the hover overlay so moving the cursor over an
// already selected edge still reads.
const SELECTED_EDGE_MATERIAL_DEFINITION: LineMaterialParameters = {
	color: 0x185fa5,
	linewidth: SELECTED_EDGE_WIDTH_PX,
	transparent: true,
	depthTest: false,
	depthWrite: false,
};

class Polyline extends Shape {
	public readonly sceneObject = new THREE.Group();

	private labelColor: string;
	private readonly lineMaterialDefinition: LineMaterialParameters;
	private lineColor: THREE.ColorRepresentation;
	private isInvalid = false;
	private vertexHandleColor: string;
	private vertexHandleFillColor: string | undefined;
	private vertexHandleHighlightColor: string | undefined;

	public readonly line: Line2;
	private readonly verticeHandles: Array<VertexHandle> = [];

	private readonly highlightedEdge: LineSegments2;
	private highlightedEdgeIndex: number | null = null;

	private readonly selectedEdges: LineSegments2;
	private selectedEdgeIndices: ReadonlyArray<number> = [];
	private highlightedVertexIndex: number | null = null;

	private readonly confirmedPoints: Array<THREE.Vector3> = [];
	private previewPoint: THREE.Vector3 | null = null;

	private isNameLabelVisible = false;
	private areDimensionLabelVisible = false;
	private areVerticesVisible = false;

	protected isLoop: boolean;
	protected maxSegments: number | null = null;

	private readonly nameLabel: Label | null = null;
	private readonly dimensionLabels: Array<Label> = [];

	constructor({
		label,
		labelColor,
		lineColor,
		vertexHandlesColor,
		vertexHandlesFillColor,
		vertexHandlesHighlightColor,
		highlightedEdgeColor,
		selectedEdgeColor,
		isLoop = false,
	}: {
		label?: string;
		labelColor?: THREE.ColorRepresentation;
		lineColor?: THREE.ColorRepresentation;
		vertexHandlesColor?: THREE.ColorRepresentation;
		vertexHandlesFillColor?: THREE.ColorRepresentation;
		vertexHandlesHighlightColor?: THREE.ColorRepresentation;
		highlightedEdgeColor?: THREE.ColorRepresentation;
		selectedEdgeColor?: THREE.ColorRepresentation;
		isLoop?: boolean;
	} = {}) {
		super();

		this.isLoop = isLoop;
		this.labelColor = labelColor?.toString() ?? DEFAULT_LABEL_COLOR;
		this.lineColor = lineColor ?? DEFAULT_LINE_COLOR;

		this.lineMaterialDefinition = {
			depthTest: true,
			depthWrite: true,
			linewidth: LINE_WIDTH_PX,
			color: this.lineColor,
		};

		this.vertexHandleColor = vertexHandlesColor?.toString() ?? DEFAULT_VERTEX_HANDLE_COLOR;
		this.vertexHandleFillColor = vertexHandlesFillColor?.toString();
		this.vertexHandleHighlightColor = vertexHandlesHighlightColor?.toString();

		this.line = createThickLine({ materialDefinition: this.lineMaterialDefinition });
		this.sceneObject.add(this.line);

		this.highlightedEdge = this.createHighlightedEdge();
		this.sceneObject.add(this.highlightedEdge);

		this.selectedEdges = this.createSelectedEdges();
		this.sceneObject.add(this.selectedEdges);

		this.setEdgeColors({ highlightedColor: highlightedEdgeColor, selectedColor: selectedEdgeColor });

		if (label !== undefined) {
			this.nameLabel = new Label({
				content: label,
				style: { backgroundColor: this.labelColor },
			});
			this.nameLabel.update({ isVisible: false });
			this.sceneObject.add(this.nameLabel.sceneObject);
		}
	}

	public dispose(): void {
		this.line.geometry.dispose();
		this.line.material.dispose();

		this.highlightedEdge.geometry.dispose();
		this.highlightedEdge.material.dispose();

		this.selectedEdges.geometry.dispose();
		this.selectedEdges.material.dispose();

		for (const handle of this.verticeHandles) {
			handle.dispose();
		}

		for (const dimensionLabel of this.dimensionLabels) {
			dimensionLabel.dispose();
		}

		if (this.nameLabel !== null) {
			this.nameLabel.dispose();
		}

		this.sceneObject.removeFromParent();
		this.sceneObject.clear();

		this.verticeHandles.length = 0;
		this.dimensionLabels.length = 0;
		this.confirmedPoints.length = 0;
		this.previewPoint = null;
		this.areVerticesVisible = false;
		this.highlightedEdgeIndex = null;
		this.highlightedVertexIndex = null;
		this.selectedEdgeIndices = [];
	}

	public isComplete(): boolean {
		return this.maxSegments !== null && this.confirmedPoints.length - 1 >= this.maxSegments;
	}

	public start({ initialPoint }: { initialPoint: THREE.Vector3 }): void {
		this.confirmedPoints.push(initialPoint.clone());
		this.previewPoint = initialPoint.clone();
		this.syncShapeView();
	}

	public override update({ currentPoint, confirmPoint = false }: { currentPoint: THREE.Vector3; confirmPoint?: boolean }): void {
		this.previewPoint = currentPoint.clone();

		if (confirmPoint === true) {
			this.confirmedPoints.push(currentPoint.clone());
		}

		this.syncShapeView();
	}

	public override finish(): void {
		this.previewPoint = null;
		this.syncShapeView();
	}

	public showVertices(): void {
		this.areVerticesVisible = true;
		this.syncShapeView();
	}

	public hideVertices(): void {
		this.areVerticesVisible = false;
		this.syncShapeView();
	}

	public setNameLabelsVisibility({ isVisible }: { isVisible: boolean }): void {
		this.isNameLabelVisible = isVisible;
		this.syncShapeView();
	}

	public setDimensionLabelVisible({ isVisible }: { isVisible: boolean }): void {
		this.areDimensionLabelVisible = isVisible;
		this.syncShapeView();
	}

	public getPoints(): Array<THREE.Vector3> {
		return this.confirmedPoints.map((point) => point.clone());
	}

	public setLabel({ content, color }: { content?: string; color?: THREE.ColorRepresentation }): void {
		if (color !== undefined) {
			this.labelColor = color.toString();
		}

		if (this.nameLabel !== null) {
			this.nameLabel.update({
				content,
				style: color !== undefined ? { backgroundColor: this.labelColor } : undefined,
			});
		}

		if (color !== undefined) {
			for (const dimensionLabel of this.dimensionLabels) {
				dimensionLabel.update({ style: { backgroundColor: this.labelColor } });
			}
		}
	}

	public setPoints({ points }: { points: Array<THREE.Vector3> }): void {
		this.confirmedPoints.length = 0;

		// Every geometry write funnels through here, so this is where coincident vertices are dropped:
		// a zero-length edge makes the offset helpers refuse the whole shape (DWEB-20).
		for (const point of dropDegenerateVertices({ points, isLoop: this.isLoop })) {
			this.confirmedPoints.push(point.clone());
		}

		this.syncShapeView();
	}

	/** A closed ring has one edge per vertex; an open chain has one fewer. */
	public getEdgeCount(): number {
		if (this.isLoop) {
			return this.confirmedPoints.length >= 3 ? this.confirmedPoints.length : 0;
		}

		return Math.max(0, this.confirmedPoints.length - 1);
	}

	/** The infinite lines the ends of an edge slide along while it is being offset. */
	public getEdgeHelperLines({ edgeIndex }: { edgeIndex: number }): {
		previous: HelperLine | null;
		next: HelperLine | null;
	} | null {
		return getEdgeHelperLines({ points: this.confirmedPoints, edgeIndex, isLoop: this.isLoop });
	}

	/** Unit direction a positive offset distance pushes the edge along. */
	public getEdgeNormal({ edgeIndex }: { edgeIndex: number }): THREE.Vector3 | null {
		return getEdgeNormal({ points: this.confirmedPoints, edgeIndex, isLoop: this.isLoop });
	}

	/**
	 * Marks the edge the cursor would grab, or clears the marker with null. Called on every pointer
	 * move, so it refreshes only the overlay — a full syncShapeView() would rebuild every geometry
	 * and reconcile the CSS2D labels.
	 */
	public setHighlightedEdge({ edgeIndex }: { edgeIndex: number | null }): void {
		if (this.highlightedEdgeIndex === edgeIndex) {
			return;
		}

		this.highlightedEdgeIndex = edgeIndex;
		this.syncHighlightedEdge();
	}

	public getHighlightedEdge(): number | null {
		return this.highlightedEdgeIndex;
	}

	/** Marks the vertex the cursor would grab, or clears the marker with null. Touches only the handles. */
	public setHighlightedVertex({ vertexIndex }: { vertexIndex: number | null }): void {
		if (this.highlightedVertexIndex === vertexIndex) {
			return;
		}

		this.highlightedVertexIndex = vertexIndex;
		this.syncVertexHighlight();
	}

	public getHighlightedVertex(): number | null {
		return this.highlightedVertexIndex;
	}

	/** Marks the edges chosen for a shared offset. Touches only the overlay, like setHighlightedEdge. */
	public setSelectedEdges({ edgeIndices }: { edgeIndices: ReadonlyArray<number> }): void {
		const next = [...edgeIndices].sort((a, b) => a - b);
		const current = this.selectedEdgeIndices;

		if (next.length === current.length && next.every((value, index) => value === current[index])) {
			return;
		}

		this.selectedEdgeIndices = next;
		this.syncSelectedEdges();
	}

	public getSelectedEdges(): ReadonlyArray<number> {
		return this.selectedEdgeIndices;
	}

	/**
	 * Slides a single edge along its normal, keeping its ends on the lines of the neighbouring
	 * edges. Returns false and leaves the geometry alone when no valid corner can be constructed.
	 */
	public offsetEdge({ edgeIndex, distance }: { edgeIndex: number; distance: number }): boolean {
		return this.offsetEdges({ edgeIndices: [edgeIndex], distance });
	}

	/**
	 * Slides several edges at once. Not a loop over offsetEdge: moving one edge changes the neighbours
	 * of the next, so the lines have to move together and the vertices be solved afterwards.
	 */
	public offsetEdges({ edgeIndices, distance }: { edgeIndices: ReadonlyArray<number>; distance: number }): boolean {
		const points = offsetPolylineEdges({
			points: this.confirmedPoints,
			edgeIndices,
			distance,
			isLoop: this.isLoop,
		});

		if (points === null) {
			return false;
		}

		this.setPoints({ points });
		return true;
	}

	/**
	 * Offsets every edge at once. Positive moves to the right of the drawing direction; `Polygon`
	 * overrides this so positive means inward.
	 */
	public offset({ distance }: { distance: number }): boolean {
		const points = offsetPolyline({ points: this.confirmedPoints, distance });

		if (points === null) {
			return false;
		}

		this.setPoints({ points });
		return true;
	}

	public getSelfIntersections(): Array<SelfIntersection> {
		return findSelfIntersections({ points: this.confirmedPoints, isLoop: this.isLoop });
	}

	/** Cuts off self-intersecting loops, keeping the largest area. Returns false if nothing changed. */
	public removeSelfIntersections(): boolean {
		if (this.getSelfIntersections().length === 0) {
			return false;
		}

		this.setPoints({ points: removeSelfIntersections({ points: this.confirmedPoints }) });
		return true;
	}

	protected afterShapeViewSynchronized(_shapeVertices: ReadonlyArray<THREE.Vector3>): void {
		// Hook for subclasses (e.g. Polygon fill).
	}

	public override select(): void {
		this.showVertices();
	}

	public override deselect(): void {
		this.hideVertices();
	}

	protected override syncShapeView(): void {
		const vertices = this.confirmedPoints.map((point) => point.clone());

		if (this.previewPoint !== null) {
			vertices.push(this.previewPoint.clone());
		}

		if (this.isLoop && vertices.length >= 3) {
			vertices.push(vertices[0].clone());
		}

		this.syncLine({ vertices });
		this.syncEndpoints();
		this.syncHighlightedEdge();
		this.syncSelectedEdges();
		this.syncNameLabel();
		this.syncDimensionLabels({ vertices });
		this.afterShapeViewSynchronized(vertices);
	}

	private syncHighlightedEdge() {
		const edgeIndex = this.highlightedEdgeIndex;

		if (edgeIndex === null || edgeIndex < 0 || edgeIndex >= this.getEdgeCount()) {
			setThickLinePoints({ line: this.highlightedEdge, points: [] });
			return;
		}

		const start = this.confirmedPoints[edgeIndex];
		const end = this.confirmedPoints[(edgeIndex + 1) % this.confirmedPoints.length];

		setThickLinePoints({ line: this.highlightedEdge, points: [start, end] });
	}

	private syncSelectedEdges() {
		const edgeCount = this.getEdgeCount();
		const endpoints: Array<THREE.Vector3> = [];

		for (const edgeIndex of this.selectedEdgeIndices) {
			if (edgeIndex < 0 || edgeIndex >= edgeCount) {
				continue;
			}

			endpoints.push(this.confirmedPoints[edgeIndex], this.confirmedPoints[(edgeIndex + 1) % this.confirmedPoints.length]);
		}

		setThickLinePoints({ line: this.selectedEdges, points: endpoints });
	}

	private createSelectedEdges(): LineSegments2 {
		const lineSegments = createThickLineSegments({ materialDefinition: SELECTED_EDGE_MATERIAL_DEFINITION });
		lineSegments.visible = false;
		return lineSegments;
	}

	private createHighlightedEdge(): LineSegments2 {
		const lineSegments = createThickLineSegments({ materialDefinition: HIGHLIGHTED_EDGE_MATERIAL_DEFINITION });
		lineSegments.visible = false;
		return lineSegments;
	}

	private syncLine({ vertices }: { vertices: Array<THREE.Vector3> }) {
		setThickLinePoints({ line: this.line, points: vertices });
	}

	public setLineColor(color: THREE.ColorRepresentation): void {
		this.lineColor = color;
		this.applyLineColor();
	}

	/** Paints the outline red while an external rule (containment etc.) flags the shape. */
	public setInvalid(isInvalid: boolean): void {
		this.isInvalid = isInvalid;
		this.applyLineColor();
	}

	protected isMarkedInvalid(): boolean {
		return this.isInvalid;
	}

	private applyLineColor(): void {
		this.line.material.color.set(this.isInvalid ? INVALID_COLOR : this.lineColor);
	}

	public setEdgeColors({ highlightedColor, selectedColor }: { highlightedColor?: THREE.ColorRepresentation; selectedColor?: THREE.ColorRepresentation }): void {
		if (highlightedColor !== undefined) {
			this.highlightedEdge.material.color.set(highlightedColor);
		}

		if (selectedColor !== undefined) {
			this.selectedEdges.material.color.set(selectedColor);
		}
	}

	public setVertexHandlesColors({
		color,
		fillColor,
		highlightColor,
	}: {
		color?: THREE.ColorRepresentation;
		fillColor?: THREE.ColorRepresentation;
		highlightColor?: THREE.ColorRepresentation;
	}): void {
		if (color !== undefined) {
			this.vertexHandleColor = color.toString();
		}

		if (fillColor !== undefined) {
			this.vertexHandleFillColor = fillColor.toString();
		}

		if (highlightColor !== undefined) {
			this.vertexHandleHighlightColor = highlightColor.toString();
		}

		for (const handle of this.verticeHandles) {
			handle.setColors({
				color: this.vertexHandleColor,
				fillColor: this.vertexHandleFillColor,
				highlightColor: this.vertexHandleHighlightColor,
			});
		}
	}

	private syncVertexHighlight() {
		for (let index = 0; index < this.verticeHandles.length; index++) {
			this.verticeHandles[index].update({ isHighlighted: index === this.highlightedVertexIndex });
		}
	}

	private syncEndpoints() {
		while (this.verticeHandles.length < this.confirmedPoints.length) {
			const handle = new VertexHandle({
				color: this.vertexHandleColor,
				fillColor: this.vertexHandleFillColor,
				highlightColor: this.vertexHandleHighlightColor,
			});
			this.verticeHandles.push(handle);
			this.sceneObject.add(handle.sceneObject);
		}

		while (this.verticeHandles.length > this.confirmedPoints.length) {
			const handle = this.verticeHandles.pop();

			if (handle) {
				handle.dispose();
			}
		}

		for (let index = 0; index < this.verticeHandles.length; index++) {
			this.verticeHandles[index].update({
				position: this.confirmedPoints[index],
				isVisible: this.areVerticesVisible,
				isHighlighted: index === this.highlightedVertexIndex,
			});
		}
	}

	private syncNameLabel() {
		if (this.nameLabel === null) {
			return;
		}

		if (this.previewPoint) {
			this.nameLabel.update({ isVisible: false });
			return;
		}

		const centroid = new THREE.Vector3();

		for (const point of this.confirmedPoints) {
			centroid.add(point);
		}

		centroid.divideScalar(this.confirmedPoints.length);
		centroid.setY(-10);

		this.nameLabel.update({ isVisible: this.isNameLabelVisible, position: centroid });
	}

	private syncDimensionLabels({ vertices }: { vertices: Array<THREE.Vector3> }) {
		const segmentCount = Math.max(0, vertices.length - 1);

		while (this.dimensionLabels.length < segmentCount) {
			const dimensionLabel = new Label({
				content: "-",
				style: { borderRadius: "8px", padding: "2px 4px", backgroundColor: this.labelColor },
			});
			this.dimensionLabels.push(dimensionLabel);
			this.sceneObject.add(dimensionLabel.sceneObject);
		}

		while (this.dimensionLabels.length > segmentCount) {
			const dimensionLabel = this.dimensionLabels.pop();

			if (dimensionLabel) {
				dimensionLabel.dispose();
			}
		}

		for (let index = 0; index < segmentCount; index++) {
			const start = vertices[index];
			const end = vertices[index + 1];
			const midpoint = start.clone().lerp(end, 0.5);
			const length = start.distanceTo(end);

			const dimensionLabel = this.dimensionLabels[index];
			dimensionLabel.update({
				position: midpoint,
				content: `${Number(length.toFixed(2))} m`,
				isVisible: length > 0 && this.areDimensionLabelVisible,
			});
		}
	}
}

export { Polyline };
